import { Controller } from '@hotwired/stimulus';
import { initMask } from '../../packs/mask';
import { selectselectize } from '../../packs/custom_selectize';

// Connects to data-controller="forms--ciot"
export default class extends Controller {
  static get targets() {
    return [
      'parentCiotWrapper',
      'documentType',
      'identifierMount',
      'identifierTemplate',
      'route',
      'suspendedAxles',
      'cityOrigin',
      'cityDestiny',
      'routeError',
      'freightValue',
      'tollVoucherAmount',
      'vpr',
      'parentCiotSelect',
      'bitremRadio',
      'axlesField',
      'grossWeightField',
      'loadValueField',
      'totalFareField',
    ];
  }

  static get values() {
    return {
      fetchRoutesUrl: String,
      totalFare: Number,
      supplierFreight: Number,
      axles: Number,
      grossWeight: Number,
      loadValue: Number,
      supplierToll: Number,
      vprPaidByClient: Boolean,
    };
  }

  connect() {
    this.renderIdentifier(this.documentTypeTarget.value);
    this.fetchRoutes();
  }

  toggleParentCiot(event) {
    const button = event.currentTarget;
    const { value } = button.dataset;

    if (value === 'new') {
      this.parentCiotWrapperTarget.classList.add('d-none');
      this.parentCiotSelectTarget.selectize.clear();
    } else {
      this.parentCiotWrapperTarget.classList.remove('d-none');
    }
  }

  changeDocumentType(event) {
    this.renderIdentifier(event.currentTarget.value);
  }

  selectBitrem(event) {
    const radio = event.currentTarget.querySelector('.bitrem-radio');

    if (radio) radio.checked = true;

    this.axlesFieldTarget.value = this.totalAxles;
    this.grossWeightFieldTarget.value = this.totalGrossWeight;
    this.loadValueFieldTarget.value = this.formatMoney(this.totalLoadValue);
    this.freightValueTarget.value = this.formatMoney(
      this.totalSupplierFreightValue
    );
    this.totalFareFieldTarget.value = this.formatMoney(
      this.totalFareWithBitremValue
    );
    this.fetchRoutes();
    this.dispatchChangeEvent();
  }

  renderIdentifier(documentType) {
    const template = this.identifierTemplateTargets.find(
      (element) => element.dataset.documentType === documentType
    );

    this.identifierMountTarget.innerHTML = '';

    if (template) {
      this.identifierMountTarget.appendChild(template.content.cloneNode(true));
    }

    selectselectize();
    initMask();
  }

  async fetchRoutes() {
    const cityOriginId = this.cityOriginTarget.value;
    const cityDestinyId = this.cityDestinyTarget.value;

    if (!cityOriginId || !cityDestinyId) return;

    const params = new URLSearchParams({
      city_origin_id: cityOriginId,
      city_destiny_id: cityDestinyId,
      axles: this.activeAxles,
    });

    const response = await fetch(`${this.fetchRoutesUrlValue}?${params}`);

    if (response.ok) {
      const routes = await response.json();
      this.populateRoutes(routes);
      this.routeErrorTarget.classList.add('d-none');
    } else {
      this.routeErrorTarget.classList.remove('d-none');
    }
  }

  populateRoutes(routes) {
    const select = this.routeTarget.selectize;

    select.settings.render.option = (item, escape) => `
      <div class="p-2">
        <div class="font-weight-bold">
          ${escape(item.text)}
        </div>
        <div class="text-muted small">
          ${escape(item.name)}
        </div>
      </div>
    `;

    select.clearOptions();
    select.clear(true);

    routes.forEach((route) => {
      select.addOption({
        value: `${route.RouteCode} - ${route.TraceCode}`,
        text: `${route.TraceIdentifier} - ${this.formatMoney(
          route.TotalVPRValue,
          true
        )}`,
        name: route.Name,
        vprValue: route.TotalVPRValue,
      });
    });

    select.refreshOptions(false);

    this.tollVoucherAmountTarget.value = '';
    this.dispatchChangeEvent();
  }

  calculateFreightValue() {
    const select = this.routeTarget.selectize;
    const option = select.options[select.getValue()];

    if (!option) return;

    const totalFare = Number(this.totalFareWithBitremValue) || 0;
    const tollVoucherAmount = this.hasVpr ? Number(option.vprValue) || 0 : 0;

    const calculatedFreight = this.hasVpr
      ? totalFare - tollVoucherAmount
      : totalFare;

    this.tollVoucherAmountTarget.value = this.formatMoney(tollVoucherAmount);
    this.freightValueTarget.value = this.formatMoney(calculatedFreight);

    this.dispatchChangeEvent();
  }

  formatMoney(value, withCurrency = false) {
    return Number(value).toLocaleString('pt-BR', {
      style: withCurrency ? 'currency' : 'decimal',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  dispatchChangeEvent() {
    this.tollVoucherAmountTarget.dispatchEvent(new Event('change'));
    this.freightValueTarget.dispatchEvent(new Event('change'));
  }

  selectedBitremValue(key) {
    if (!this.hasBitremRadioTarget) return 0;

    const checked = this.bitremRadioTargets.find((radio) => radio.checked);

    return checked ? Number(checked.dataset[key]) || 0 : 0;
  }

  get totalAxles() {
    return this.axlesValue + this.selectedBitremValue('axles');
  }

  get totalGrossWeight() {
    return this.grossWeightValue + this.selectedBitremValue('grossWeight');
  }

  get totalLoadValue() {
    return this.loadValueValue + this.selectedBitremValue('loadValue');
  }

  get totalSupplierFreightValue() {
    return (
      this.supplierFreightValue + this.selectedBitremValue('supplierFreight')
    );
  }

  get totalSupplierTollValue() {
    return this.supplierTollValue + this.selectedBitremValue('supplierToll');
  }

  get totalFareWithBitremValue() {
    const total = this.totalFareValue + this.selectedBitremValue('totalFare');

    return this.vprPaidByClientValue
      ? total - this.totalSupplierTollValue / 2
      : total;
  }

  get activeAxles() {
    const suspendedAxles = Number(this.suspendedAxlesTarget.value) || 0;

    return this.totalAxles - suspendedAxles;
  }

  get hasVpr() {
    return Boolean(this.vprTarget.checked);
  }
}
